import { useState, useEffect, useCallback } from "react";
import { Loader2, AlertCircle, RefreshCw, Filter } from "lucide-react";
import { ApiService } from "../../../../services/api";
import { LessonOption, TopicOption, StudentReportType } from "../../../../types/student-report";
import StudentSelector from "./StudentSelector";
import StudentReportDisplay from "./StudentReportDisplay";

interface StudentReportsTabProps {
  selectedStudent: string | null;
  onStudentChange: (studentId: string | null) => void;
  availableLessons: LessonOption[];
  availableTopics: TopicOption[];
  selectedLessonFilter: string | null;
  onLessonFilterChange: (lessonId: string | null) => void;
  selectedTopicFilter: string | null;
  onTopicFilterChange: (topicId: string | null) => void;
  selectedReportType: StudentReportType;
  onReportTypeChange: (type: StudentReportType) => void;
  onRefresh: () => void;
}

export default function StudentReportsTab({
  selectedStudent,
  onStudentChange,
  availableLessons,
  availableTopics,
  selectedLessonFilter,
  onLessonFilterChange,
  selectedTopicFilter,
  onTopicFilterChange,
  selectedReportType,
  onReportTypeChange,
  onRefresh,
}: StudentReportsTabProps) {
  const [error, setError] = useState<string | null>(null);
  const [filtersLoading, setFiltersLoading] = useState(false);
  const [filtered, setFiltered] = useState(false);

  useEffect(() => {
    if (selectedStudent) {
      loadAvailableLessons();
    }
  }, [selectedStudent]);

  useEffect(() => {
    if (selectedStudent && selectedLessonFilter) {
      loadAvailableTopics();
    } else {
      if (selectedTopicFilter) {
        onTopicFilterChange(null);
      }
    }
  }, [selectedStudent, selectedLessonFilter]);

  const loadAvailableLessons = async () => {
    if (!selectedStudent) return;

    try {
      setFiltersLoading(true);
      const lessons = await ApiService.getStudentAvailableLessons(selectedStudent);
    } catch (err: any) {
      console.error("Error loading available lessons:", err);
      setError("Ders listesi yüklenirken hata oluştu");
    } finally {
      setFiltersLoading(false);
    }
  };

  const loadAvailableTopics = async () => {
    if (!selectedStudent || !selectedLessonFilter) return;

    try {
      setFiltersLoading(true);
      const topics = await ApiService.getStudentAvailableTopics(selectedStudent, selectedLessonFilter);
    } catch (err: any) {
      console.error("Error loading available topics:", err);
      setError("Konu listesi yüklenirken hata oluştu");
    } finally {
      setFiltersLoading(false);
    }
  };

  const isFilterReady = () => {
    return selectedStudent;
  };

  const buildMissingMessage = () => {
    if (!selectedStudent) return "Öğrenci seçiniz.";
    return "";
  };

  const handleFilter = () => {
    if (isFilterReady()) {
      setFiltered(true);
      onRefresh();
    }
  };

  const handleClearFilters = useCallback(() => {
    onLessonFilterChange(null);
    onTopicFilterChange(null);
    onReportTypeChange("all");
    setFiltered(false);
  }, [onLessonFilterChange, onTopicFilterChange, onReportTypeChange]);

  const handleStudentChange = useCallback(
    (studentId: string | null) => {
      onLessonFilterChange(null);
      onTopicFilterChange(null);
      onReportTypeChange("all");
      setError(null);
      setFiltered(false);
      onStudentChange(studentId);
    },
    [onStudentChange, onLessonFilterChange, onTopicFilterChange, onReportTypeChange]
  );

  const handleLessonFilterChange = useCallback(
    (lessonId: string | null) => {
      onLessonFilterChange(lessonId);
      onTopicFilterChange(null);
    },
    [onLessonFilterChange, onTopicFilterChange]
  );

  const renderEmptyState = () => null;

  const renderErrorState = () => (
    <div className="flex flex-col items-center justify-center py-12 text-center">
      <AlertCircle className="w-16 h-16 text-red-300 mb-4" />
      <h3 className="text-lg font-medium text-gray-900 mb-2">Hata Oluştu</h3>
      <p className="text-gray-500 max-w-md mb-4">{error}</p>
      <button
        onClick={() => {
          setError(null);
          onRefresh();
        }}
        className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">
        <RefreshCw className="w-4 h-4 mr-2" />
        Tekrar Dene
      </button>
    </div>
  );

  const renderNoDataState = () => (
    <div className="flex flex-col items-center justify-center py-12 text-center">
      <AlertCircle className="w-16 h-16 text-yellow-300 mb-4" />
      <h3 className="text-lg font-medium text-gray-900 mb-2">Veri Bulunamadı</h3>
      <p className="text-gray-500 max-w-md mb-4">Bu öğrenci için rapor verisi bulunamadı. Öğrenci henüz hiç test almamış olabilir.</p>
      <button
        onClick={onRefresh}
        className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">
        <RefreshCw className="w-4 h-4 mr-2" />
        Yenile
      </button>
    </div>
  );

  return (
    <div className="h-full flex flex-col">
      {!filtered && (
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-6 mb-6">
          <div className="flex items-center gap-3 mb-4">
            <Filter className="w-5 h-5 text-gray-600" />
            <h3 className="text-lg font-semibold text-gray-900">Filtreler</h3>
          </div>

          <div className="max-h-96 overflow-y-auto">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 xl:grid-cols-4 gap-4 mb-6">
              <div className="col-span-1">
                <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">Öğrenci</label>
                <StudentSelector
                  selectedStudent={selectedStudent}
                  onStudentChange={handleStudentChange}
                  placeholder="Öğrenci seçin..."
                  inline={true}
                />
              </div>

              <div className="col-span-1">
                <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">Ders</label>
                <div>
                  <div className="border border-gray-300 rounded-lg overflow-hidden">
                    <div className="max-h-64 overflow-auto divide-y divide-gray-200">
                      <button
                        type="button"
                        onClick={() => handleLessonFilterChange(null)}
                        className={`w-full text-left px-3 py-2 text-sm transition-colors ${
                          !selectedLessonFilter ? "bg-blue-50 text-blue-900" : "hover:bg-gray-50 text-gray-800"
                        }`}
                        disabled={!selectedStudent}>
                        Tümü
                      </button>
                      {availableLessons.map((lesson) => {
                        const selected = lesson.id === selectedLessonFilter;
                        return (
                          <button
                            key={lesson.id}
                            type="button"
                            onClick={() => handleLessonFilterChange(lesson.id)}
                            className={`w-full text-left px-3 py-2 text-sm transition-colors ${
                              selected ? "bg-blue-50 text-blue-900" : "hover:bg-gray-50 text-gray-800"
                            }`}
                            disabled={!selectedStudent}>
                            {lesson.title}
                          </button>
                        );
                      })}
                      {selectedStudent && availableLessons.length === 0 && <div className="px-3 py-2 text-sm text-gray-500">Ders yok.</div>}
                      {!selectedStudent && <div className="px-3 py-2 text-sm text-gray-500">Ders yok.</div>}
                    </div>
                  </div>
                </div>
              </div>

              <div className="col-span-1">
                <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">Konu</label>
                <div>
                  <div className="border border-gray-300 rounded-lg overflow-hidden">
                    <div className="max-h-64 overflow-auto divide-y divide-gray-200">
                      <button
                        type="button"
                        onClick={() => onTopicFilterChange(null)}
                        className={`w-full text-left px-3 py-2 text-sm transition-colors ${
                          !selectedTopicFilter ? "bg-blue-50 text-blue-900" : "hover:bg-gray-50 text-gray-800"
                        }`}
                        disabled={!selectedStudent}>
                        Tümü
                      </button>
                      {selectedLessonFilter &&
                        availableTopics.map((topic) => {
                          const selected = topic.id === selectedTopicFilter;
                          return (
                            <button
                              key={topic.id}
                              type="button"
                              onClick={() => onTopicFilterChange(topic.id)}
                              className={`w-full text-left px-3 py-2 text-sm transition-colors ${
                                selected ? "bg-blue-50 text-blue-900" : "hover:bg-gray-50 text-gray-800"
                              }`}
                              disabled={!selectedStudent}>
                              {topic.title}
                            </button>
                          );
                        })}
                      {selectedStudent && selectedLessonFilter && availableTopics.length === 0 && (
                        <div className="px-3 py-2 text-sm text-gray-500">Konu yok.</div>
                      )}
                      {!selectedLessonFilter && <div className="px-3 py-2 text-sm text-gray-500">Konu yok.</div>}
                    </div>
                  </div>
                </div>
              </div>

              <div className="col-span-1">
                <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">Test Türü</label>
                <div>
                  <div className="border border-gray-300 rounded-lg overflow-hidden">
                    <div className="max-h-64 overflow-auto divide-y divide-gray-200">
                      <button
                        type="button"
                        onClick={() => onReportTypeChange("all")}
                        className={`w-full text-left px-3 py-2 text-sm transition-colors ${
                          selectedReportType === "all" ? "bg-blue-50 text-blue-900" : "hover:bg-gray-50 text-gray-800"
                        }`}
                        disabled={!selectedStudent}>
                        Tümü
                      </button>
                      <button
                        type="button"
                        onClick={() => onReportTypeChange("homework")}
                        className={`w-full text-left px-3 py-2 text-sm transition-colors ${
                          selectedReportType === "homework" ? "bg-blue-50 text-blue-900" : "hover:bg-gray-50 text-gray-800"
                        }`}
                        disabled={!selectedStudent}>
                        Ödevler
                      </button>
                      <button
                        type="button"
                        onClick={() => onReportTypeChange("independent")}
                        className={`w-full text-left px-3 py-2 text-sm transition-colors ${
                          selectedReportType === "independent" ? "bg-blue-50 text-blue-900" : "hover:bg-gray-50 text-gray-800"
                        }`}
                        disabled={!selectedStudent}>
                        Testler
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 mt-4">
            <div className="flex items-center gap-3">
              <button
                onClick={handleFilter}
                disabled={!isFilterReady()}
                className={`px-6 py-2 rounded-lg font-medium text-sm transition-colors ${
                  isFilterReady() ? "bg-blue-600 hover:bg-blue-700 text-white" : "bg-gray-300 text-gray-500 cursor-not-allowed"
                }`}>
                Filtrele
              </button>
              {!isFilterReady() && (
                <span className="text-xs text-gray-600">Öğrenci raporlarını görüntülemek için lütfen {buildMissingMessage()}</span>
              )}
            </div>
          </div>
        </div>
      )}

      {filtered && (
        <div className="flex justify-start mb-6">
          <div className="flex flex-row flex-wrap gap-2">
            <button
              onClick={handleClearFilters}
              className="px-3 sm:px-4 py-2 rounded-md text-xs sm:text-sm font-medium border flex-shrink-0 inline-flex items-center justify-center gap-1.5 sm:gap-2 bg-white text-gray-700 hover:bg-gray-50 border-gray-300">
              <Filter className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
              <span>Filtrele</span>
            </button>
          </div>
        </div>
      )}

      <div className="flex-1 min-h-0 overflow-y-auto">
        {!filtered ? (
          <div className="flex-1 flex items-center justify-center h-full">{renderEmptyState()}</div>
        ) : error ? (
          renderErrorState()
        ) : !selectedStudent ? (
          renderNoDataState()
        ) : (
          <StudentReportDisplay
            userId={selectedStudent}
            reportType={selectedReportType}
            selectedMainLessonFilter={selectedLessonFilter}
            selectedTopicFilter={selectedTopicFilter}
          />
        )}
      </div>
    </div>
  );
}
